"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Info } from "lucide-react";

import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { PickupDateRail } from "@/components/ui/pickup-date-rail";
import { Skeleton } from "@/components/ui/skeleton";
import { TimeSlotPicker } from "@/components/ui/time-slot-picker";
import {
  getCakeDateAvailabilityStateForCart,
  hasBentoCakeSize,
  hasRegularCakeSize,
  isMerchPickupDateSelectable,
  type PickupAvailabilityRecord,
} from "@/lib/pickupAvailability";
import {
  concretePickupTime,
  intersectPickupWindows,
  isTimeWithinWindow,
  isUsablePickupWindow,
  type PickupWindow,
} from "@/lib/pickup-time";

// Merch-only orders ("merch") carry no cake/cupcake capacity; the date-rail /
// calendar `hasCake` checks correctly treat it as non-cake (see Phase 1 plan).
export type PickupOrderType =
  | "cake"
  | "cake-and-cupcakes"
  | "cupcakes"
  | "merch";

export interface PickupDateTimePickerProps {
  /** Currently selected ISO date (YYYY-MM-DD) or empty string / null. */
  date: string | null;
  /** Currently selected time (HH:MM or "custom:HH:MM") or empty string. */
  time: string;
  /** Called with the new ISO date. The component does NOT clear the time for you. */
  onDateChange: (isoDate: string) => void;
  /** Called with the new time value. */
  onTimeChange: (time: string) => void;

  /** Domain context driving availability computation. */
  orderType?: PickupOrderType;
  cakeServings?: string;
  /**
   * Configured cake sizes for the current product. When provided, auto-select
   * and the rail both filter availability against these options. Leave
   * undefined in the order flow where only the user-selected `cakeServings`
   * matters.
   */
  cakeAllowedServings?: readonly string[];

  /**
   * Effective bento mapping from the admin order-options: the set of serving
   * VALUES that count toward the BENTO capacity bucket. When provided, the
   * bento/regular split for date selectability follows this set (so a custom
   * non-`1-4` range marked bento is judged against the bento counters); when
   * omitted, classification falls back to the legacy `value === "1-4"` rule.
   * See `hasBentoCakeSize` in lib/pickupAvailability.ts.
   */
  bentoServings?: ReadonlySet<string>;

  /**
   * Cart-aware capacity filter — quantity of bento (`1-4`) cakes the
   * customer's full cart needs. Dates whose remaining bento capacity is
   * insufficient are marked as full so the customer can't pick a date
   * the server-side cart-aggregate gate will reject at submit. Defaults
   * to 0 (no filter — legacy single-cake callers).
   */
  requiredBentoSlots?: number;
  /** Same as `requiredBentoSlots` but for the regular-cake bucket. */
  requiredRegularSlots?: number;

  /**
   * Additive merch pickup-availability constraint (Phase C1,
   * `docs/merch-pickup-availability-plan.md` §4). The set of ISO dates allowed by
   * the merch per-product intersection for the CURRENTLY-CHOSEN location. When
   * non-null, a date must ALSO be in this set to be selectable — additive on top
   * of the cake rules (merch-only: open slot AND in-set; mixed: cake-available
   * AND in-set). `null`/omitted means NO merch constraint, so cake-only /
   * cupcake carts are byte-identical. The parent computes this from
   * `getMerchPickupIntersectionForCart` (`lib/merch-pickup.ts`).
   */
  merchAllowedDates?: ReadonlySet<string> | null;

  /**
   * Companion to {@link merchAllowedDates}: the merch products' INTERSECTED
   * collection window per allowed ISO date (Codex P2, PR #118). The TIME
   * dropdown is generated from the SELECTED record's `startTime`/`endTime`,
   * which on a mixed cake+merch cart is the CAKE slot's window — so without
   * this the picker happily offered 12:00–18:00 for a merch product that can
   * only be collected 14:00–16:00. When a date has an entry here, the offered
   * window is the overlap of the cake record's window and the merch window.
   * `null`/omitted → no merch time constraint (cake-only stays byte-identical).
   */
  merchAllowedWindows?: ReadonlyMap<string, PickupWindow> | null;

  /**
   * Availability records (owned by the parent). Pass from
   * `usePickupAvailability()` so downstream consumers can reuse the same
   * data (e.g. for disabling serving-size options).
   */
  availabilityRecords: PickupAvailabilityRecord[];

  /**
   * True while the parent is (re)fetching availability. When set, the
   * "no upcoming dates" amber alert is suppressed so users don't see a
   * jarring empty state during the brief window where SSR-cached / stale
   * data is being replaced by a fresh fetch (e.g. right after an admin
   * pickup-availability update). The date rail still renders its own
   * loading affordance.
   */
  isAvailabilityLoading?: boolean;

  /** Earliest selectable ISO date (defaults to today). */
  minDate?: string;
  /** Number of date chips to show in the rail (defaults to 21). */
  daysToShow?: number;

  /** Automatically select the first available upcoming date when none is set. */
  autoSelectFirstAvailable?: boolean;
  /**
   * Automatically select the first time slot whenever no time is chosen
   * (initial mount or after a date change). Defaults to `true` to preserve
   * existing single-page checkout UX. Set to `false` in step-based flows
   * where picking the time is a deliberate user action that triggers
   * navigation to the next step.
   */
  autoSelectFirstTime?: boolean;

  /** External error message (e.g. from react-hook-form) rendered below the rail. */
  dateError?: ReactNode;
  /** External error message for the time slot, rendered below the picker. */
  timeError?: ReactNode;
  /**
   * Optional custom label for the date field. Pass `null` or `false` to hide
   * the internal default, or a `ReactNode` to override it. `undefined` uses
   * the default translation.
   */
  dateLabel?: ReactNode;
  /** Optional custom label for the time field — same semantics as `dateLabel`. */
  timeLabel?: ReactNode;
  /** Hide the time picker entirely (date-only mode). */
  hideTimeSlot?: boolean;

  className?: string;
}

function isoDateFromDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Loading affordance shown in place of the date rail while availability is
 * being fetched and we have nothing to render yet. Mirrors the rail's chip
 * dimensions (`min-w-[78px]` × ~64px tall, padded scroller) so the layout
 * doesn't shift when the real data arrives.
 */
function PickupDateRailSkeleton({
  headerLabel,
  loadingLabel,
}: {
  headerLabel: ReactNode;
  loadingLabel: string;
}) {
  return (
    <div
      className="space-y-2"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {headerLabel}
      <div
        className="flex gap-2.5 overflow-hidden px-10 py-3 sm:px-12"
        aria-hidden="true"
      >
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton
            key={index}
            className="h-[64px] w-[78px] shrink-0 rounded-xl"
          />
        ))}
      </div>
      <span className="sr-only">{loadingLabel}</span>
    </div>
  );
}

/**
 * Reusable controlled date + time picker for pickup scheduling.
 *
 * Wraps `PickupDateRail` (horizontal chips + "More dates" calendar popover)
 * and `TimeSlotPicker` into a single plug-and-play unit used on the cake
 * product page and in the order flow form.
 *
 * Contract:
 * - Parent owns state (`date`, `time`, `onDateChange`, `onTimeChange`) so it
 *   works with both `useState` and react-hook-form.
 * - Parent owns the availability fetch (pass `availabilityRecords` from
 *   `usePickupAvailability()`) so downstream consumers can reuse the same
 *   data for unrelated UI (e.g. disabling serving-size options).
 * - Translations are read internally from the `orderForm` next-intl
 *   namespace. For reuse in admin or reschedule contexts with different
 *   namespaces, extract labels as props (matching `PickupDateRail`'s API).
 */
export function PickupDateTimePicker({
  date,
  time,
  onDateChange,
  onTimeChange,
  orderType,
  cakeServings,
  cakeAllowedServings,
  bentoServings,
  requiredBentoSlots = 0,
  requiredRegularSlots = 0,
  merchAllowedDates = null,
  merchAllowedWindows = null,
  availabilityRecords,
  isAvailabilityLoading = false,
  minDate,
  daysToShow = 21,
  autoSelectFirstAvailable = true,
  autoSelectFirstTime = true,
  dateError,
  timeError,
  dateLabel,
  timeLabel,
  hideTimeSlot = false,
  className,
}: PickupDateTimePickerProps) {
  const t = useTranslations("orderForm");
  const locale = useLocale();

  const today = useMemo(() => isoDateFromDate(new Date()), []);
  const effectiveMinDate = minDate ?? today;

  // Mirror the latest `onDateChange` callback in a ref. The parent commonly
  // passes an inline arrow (`(iso) => field.onChange(iso)`) which is a new
  // reference on every render. Reading through a ref keeps the auto-select
  // effect below from re-firing on every parent render.
  const onDateChangeRef = useRef(onDateChange);
  useEffect(() => {
    onDateChangeRef.current = onDateChange;
  }, [onDateChange]);

  // Merch-only carts ("merch") consume ZERO cake capacity: they still need a
  // valid OPEN pickup slot (date + location) but the bento/regular counters
  // must NOT gate date selection. A merch-only caller passes an empty (or
  // undefined) `cakeAllowedServings` — but an empty array is still truthy, so
  // the cake-capacity branches below would compute `hasBentoOption ===
  // hasRegularOption === false` and classify EVERY date as closed, leaving the
  // customer unable to pick a date and therefore unable to submit. Gate the
  // cake-capacity logic behind a single flag that is only active for cake
  // orders with a non-empty allow-list. When inactive, the rail's
  // `computeStatus` (which already treats `orderType === "merch"` as non-cake)
  // decides selectability from the slot's OPEN state alone. Robust regardless
  // of caller: even if a merch caller forgets to drop `cakeAllowedServings`,
  // the `orderType` check below disables the gate.
  const isMerchOrder = orderType === "merch";
  const cakeCapacityActive =
    !isMerchOrder &&
    Boolean(cakeAllowedServings && cakeAllowedServings.length > 0);

  const hasBentoOption = useMemo(
    () => hasBentoCakeSize(cakeAllowedServings, bentoServings),
    [cakeAllowedServings, bentoServings],
  );
  const hasRegularOption = useMemo(
    () => hasRegularCakeSize(cakeAllowedServings, bentoServings),
    [cakeAllowedServings, bentoServings],
  );

  const findAvailabilityByIso = useCallback(
    (iso: string) =>
      availabilityRecords.find((entry) => entry.date === iso),
    [availabilityRecords],
  );

  const selectedRecord = date ? findAvailabilityByIso(date) : undefined;

  // Auto-select the first upcoming selectable date when the parent has no date set.
  useEffect(() => {
    if (!autoSelectFirstAvailable) return;
    if (date) return;
    if (availabilityRecords.length === 0) return;

    const upcoming = availabilityRecords.filter(
      (record) => record.date >= effectiveMinDate,
    );

    const firstAvailable = upcoming.find((record) => {
      // Additive merch constraint (Phase C1): a date the merch products can't
      // fulfil is never auto-selected. `null` → no constraint.
      if (merchAllowedDates && !merchAllowedDates.has(record.date)) return false;
      // Merch-only: any OPEN slot is selectable (zero cake capacity needed).
      if (isMerchOrder) return isMerchPickupDateSelectable(record);
      const state = getCakeDateAvailabilityStateForCart(record, {
        hasBentoOption: cakeCapacityActive ? hasBentoOption : true,
        hasRegularOption: cakeCapacityActive ? hasRegularOption : true,
        requiredBentoSlots,
        requiredRegularSlots,
      });
      return state.status === "available" || state.status === "limited";
    });

    if (firstAvailable) {
      // Read through a ref so a parent that passes an unstable inline
      // arrow (e.g. `onDateChange={(iso) => field.onChange(iso)}`) doesn't
      // force this effect to re-run on every render — without the ref it
      // would re-evaluate `firstAvailable` after every parent re-render
      // and, worst case, race with form-validation re-renders into a
      // max-update-depth loop.
      onDateChangeRef.current(firstAvailable.date);
    }
  }, [
    autoSelectFirstAvailable,
    date,
    availabilityRecords,
    effectiveMinDate,
    isMerchOrder,
    cakeCapacityActive,
    hasBentoOption,
    hasRegularOption,
    requiredBentoSlots,
    requiredRegularSlots,
    merchAllowedDates,
  ]);

  const handleDateSelect = useCallback(
    (isoDate: string) => {
      // Additive merch constraint (Phase C1): block dates outside the merch
      // intersection regardless of cake state. `null` → no constraint.
      if (merchAllowedDates && !merchAllowedDates.has(isoDate)) return;
      // Guard against picking full/closed dates (rail disables them, but
      // the "More dates" popover calendar can theoretically still trigger).
      // Skipped for merch-only carts (cake capacity inactive): the rail
      // already classifies merch dates from the slot's OPEN state alone, so
      // re-running the cake-capacity gate here would wrongly block selection.
      const record = findAvailabilityByIso(isoDate);
      if (isMerchOrder) {
        // Merch-only: only block dates that aren't an OPEN slot; cake
        // capacity is irrelevant.
        if (!isMerchPickupDateSelectable(record)) return;
      } else if (cakeCapacityActive) {
        const state = getCakeDateAvailabilityStateForCart(record, {
          hasBentoOption,
          hasRegularOption,
          requiredBentoSlots,
          requiredRegularSlots,
        });
        if (state.status === "full" || state.status === "closed") {
          return;
        }
      }
      onDateChange(isoDate);
    },
    [
      findAvailabilityByIso,
      isMerchOrder,
      cakeCapacityActive,
      hasBentoOption,
      hasRegularOption,
      requiredBentoSlots,
      requiredRegularSlots,
      merchAllowedDates,
      onDateChange,
    ],
  );

  const noAvailableDates = useMemo(() => {
    if (availabilityRecords.length === 0) return true;

    // Check if any upcoming record is actually selectable
    const upcoming = availabilityRecords.filter(
      (r) => r.date >= effectiveMinDate,
    );
    return !upcoming.some((record) => {
      // Additive merch constraint (Phase C1): a date outside the merch
      // intersection is not selectable. `null` → no constraint.
      if (merchAllowedDates && !merchAllowedDates.has(record.date)) return false;
      // Merch-only: any OPEN slot counts as an available date.
      if (isMerchOrder) return isMerchPickupDateSelectable(record);
      const state = getCakeDateAvailabilityStateForCart(record, {
        hasBentoOption: cakeCapacityActive ? hasBentoOption : true,
        hasRegularOption: cakeCapacityActive ? hasRegularOption : true,
        requiredBentoSlots,
        requiredRegularSlots,
      });
      return state.status === "available" || state.status === "limited";
    });
  }, [
    availabilityRecords,
    effectiveMinDate,
    isMerchOrder,
    cakeCapacityActive,
    hasBentoOption,
    hasRegularOption,
    requiredBentoSlots,
    requiredRegularSlots,
    merchAllowedDates,
  ]);

  // `undefined` → fall back to default label; `null` or `false` → render nothing.
  const resolvedDateLabel =
    dateLabel === undefined ? (
      <Label className="text-sm font-medium">{t("date.pickupDate")}</Label>
    ) : (
      dateLabel
    );
  const resolvedTimeLabel =
    timeLabel === undefined ? (
      <Label className="text-sm font-medium">{t("date.pickupTime")}</Label>
    ) : (
      timeLabel
    );

  // The window the TIME dropdown offers for the selected date (Codex P2,
  // PR #118). Base is the selected availability record's own window — the CAKE
  // slot's window on a mixed cart, the merch-intersection window on a
  // merch-only cart. When the cart also carries a merch time constraint for
  // that date, narrow to the OVERLAP so a customer can't pick a time one of the
  // merch products cannot be collected at.
  //
  // `disjoint` is rare but REACHABLE: the parent narrows `merchAllowedDates`
  // with the same overlap test, so such a date can't be newly PICKED — but a
  // date already selected when the merch calendar (or the cake slot's window)
  // changes underneath stays in the form until the customer moves it. It is an
  // explicit state rather than "pass the inverted range through" because
  // `TimeSlotPicker.generateSlots` silently rewrites an inverted range to the
  // full 12:00–18:00 default, i.e. it would offer EVERY slot exactly when none
  // is valid. An honest explanation beats a dropdown full of unusable times.
  const timeWindow = useMemo<{
    window: PickupWindow | null;
    disjoint: boolean;
  }>(() => {
    const base: PickupWindow | null = selectedRecord
      ? {
          startTime: selectedRecord.startTime,
          endTime: selectedRecord.endTime,
        }
      : null;
    const merch = date ? merchAllowedWindows?.get(date) ?? null : null;
    // An UNUSABLE base window (inverted, e.g. 18:00–12:00, or a malformed
    // bound) means nothing is collectible on this date — the server's own gate
    // rejects every time against it — so it is `disjoint`, not a constraint to
    // intersect. It cannot WIDEN what is collectible either, so a merch window
    // does not rescue it.
    //
    // This is load-bearing, not defensive: the eviction effect below rejects
    // every time against such a window while `TimeSlotPicker.generateSlots`
    // treats `end < start` as bad input and falls back to its own 12:00–18:00
    // default. Left as a plain `{ window: base }`, the two fought each other —
    // evict, auto-select 12:00, evict — freezing the tab at 100% CPU with no
    // max-update-depth error, on a public product page.
    if (base && !isUsablePickupWindow(base)) {
      return { window: null, disjoint: true };
    }
    if (!merch) return { window: base, disjoint: false };
    if (!base) return { window: merch, disjoint: false };
    const overlap = intersectPickupWindows(base, merch);
    return overlap === null
      ? { window: null, disjoint: true }
      : { window: overlap, disjoint: false };
  }, [selectedRecord, date, merchAllowedWindows]);

  // Mirror `onTimeChange` in a ref for the same reason `onDateChange` is
  // mirrored above: the parents pass inline arrows
  // (`onTimeChange={(v) => field.onChange(v)}`), so putting the raw callback in
  // the effect's dep array would re-run it on every parent render.
  const onTimeChangeRef = useRef(onTimeChange);
  useEffect(() => {
    onTimeChangeRef.current = onTimeChange;
  }, [onTimeChange]);

  // ── STALE-TIME EVICTION (Codex P1, PR #118) ────────────────────────────────
  //
  // THE BUG. `merchAllowedWindows` arrives ASYNCHRONOUSLY (the parent fetches
  // every merch product's pickup calendar). On a mixed cart the cake half
  // resolves first, `TimeSlotPicker` auto-selects the first slot of the CAKE
  // window — say `12:00` — and only then does the merch intersection land and
  // narrow the offered window to `14:00`–`16:00`. Narrowing changes the SLOTS,
  // but `TimeSlotPicker` only auto-selects `if (!value)`, so the stale `12:00`
  // stayed in react-hook-form AND in the "selected" indicator, looking chosen,
  // all the way to submit — where the authoritative reserve
  // (`lib/merch-pickup-server.ts`, the same window gate) refused it with
  // `pickup_time_unavailable`. The customer's only clue was a submit-time error
  // naming a time the UI was still showing as valid.
  //
  // THE FIX. Whenever the resolved window no longer contains the currently
  // selected CONCRETE time, clear it — which hands control straight back to
  // `TimeSlotPicker`'s auto-select (`autoSelectFirstTime`), so on the single-page
  // checkout the time is REPLACED with the first slot of the narrowed window and
  // in the step-based flow it is left empty for a deliberate re-pick. Both are
  // the behaviour those flows already have for a date change.
  //
  // SAME PREDICATE AS THE SERVER, deliberately. `concretePickupTime` +
  // `isTimeWithinWindow` are the exact pair the reserve's gate runs, so the
  // picker can never evict a time the server would have accepted (or keep one it
  // would reject). In particular the NON-CONCRETE shapes — `custom:HH:MM` ("I
  // need a different time") and the legacy `contact` sentinel — are left ALONE:
  // they are explicit requests to arrange a hand-over outside the published
  // window, which the server also exempts. Clearing them here would silently
  // delete a deliberate customer choice.
  //
  // NO AUTO-SELECT LOOP. The replacement `TimeSlotPicker` picks is
  // `generateSlots(start, end)[0] === start`, and `isTimeWithinWindow` is
  // INCLUSIVE at both ends, so the very next run of this effect is a no-op. A
  // `disjoint` pair renders no picker at all, so nothing re-selects and the time
  // simply stays empty until the customer moves the date.
  useEffect(() => {
    if (hideTimeSlot) return;
    const concrete = concretePickupTime(time);
    if (concrete === null) return;
    // Disjoint: NOTHING is collectible on this date, so no concrete time can
    // survive. `window === null` WITHOUT `disjoint` is the ordinary "no record
    // and no merch constraint" case — `TimeSlotPicker` then falls back to its
    // own 12:00–18:00 default and there is no narrower window to enforce.
    if (!timeWindow.disjoint) {
      if (!timeWindow.window) return;
      if (isTimeWithinWindow(concrete, timeWindow.window)) return;
    }
    onTimeChangeRef.current("");
  }, [hideTimeSlot, time, timeWindow]);

  // Show skeleton chips when we're loading AND have no data to render. The
  // moment any records arrive we swap to the real rail so an in-progress
  // background revalidation doesn't blow away an already-rendered list.
  const showRailSkeleton =
    isAvailabilityLoading && availabilityRecords.length === 0;

  return (
    <div className={cn("space-y-3", className)}>
      <div className="space-y-1.5">
        {showRailSkeleton ? (
          <PickupDateRailSkeleton
            headerLabel={resolvedDateLabel}
            loadingLabel={t("date.rail.loadingDates")}
          />
        ) : (
          <PickupDateRail
            headerLabel={resolvedDateLabel}
            selectedDate={date || null}
            onDateSelect={handleDateSelect}
            availabilityRecords={availabilityRecords}
            orderType={orderType}
            cakeServings={cakeServings}
            cakeAllowedServings={cakeAllowedServings}
            bentoServings={bentoServings}
            requiredBentoSlots={requiredBentoSlots}
            requiredRegularSlots={requiredRegularSlots}
            merchAllowedDates={merchAllowedDates}
            minDate={effectiveMinDate}
            daysToShow={daysToShow}
            locale={locale}
            labels={{
              moreDates: t("date.rail.moreDates"),
              today: t("date.rail.today"),
              tomorrow: t("date.rail.tomorrow"),
              limitedBadge: t("date.rail.limitedBadge"),
              fullBadge: t("date.rail.fullBadge"),
              closedBadge: t("date.rail.closedBadge"),
              scrollPrevious: t("date.rail.scrollPrevious"),
              scrollNext: t("date.rail.scrollNext"),
              noUpcomingDates: t("date.rail.noUpcomingDates"),
              pickDate: t("date.rail.pickDate"),
              legendTitle: t("date.legend.title"),
              legendAvailable: t("date.legend.available"),
              legendLimited: t("date.legend.limited"),
              legendFullyBooked: t("date.legend.fullyBooked"),
              legendPastOrNotScheduled: t("date.legend.pastOrNotScheduled"),
            }}
          />
        )}
        {noAvailableDates && !isAvailabilityLoading && (
          <div
            role="alert"
            className="flex w-fit max-w-full items-center gap-1.5 rounded-lg border border-amber-500/60 bg-amber-50 px-3 py-1.5 text-xs text-amber-900"
          >
            <Info className="h-3 w-3 shrink-0" />
            <span>{t("date.rail.noUpcomingDates")}</span>
          </div>
        )}
        {dateError && !noAvailableDates && !isAvailabilityLoading && (
          <p className="text-xs text-destructive">{dateError}</p>
        )}
      </div>

      {!hideTimeSlot && (
        <div className="space-y-2">
          {resolvedTimeLabel}
          {timeWindow.disjoint ? (
            <div
              role="alert"
              className="flex w-fit max-w-full items-center gap-1.5 rounded-lg border border-amber-500/60 bg-amber-50 px-3 py-1.5 text-xs text-amber-900"
            >
              <Info className="h-3 w-3 shrink-0" />
              <span>{t("date.noSharedPickupWindow")}</span>
            </div>
          ) : (
            <TimeSlotPicker
              value={time}
              onChange={onTimeChange}
              startTime={timeWindow.window?.startTime}
              endTime={timeWindow.window?.endTime}
              contactLabel={t("date.pickupTimeContact")}
              contactDescription={t("date.pickupTimeContactDescription")}
              autoSelectFirstSlot={autoSelectFirstTime}
            />
          )}
          {timeError && (
            <p className="text-xs text-destructive">{timeError}</p>
          )}
        </div>
      )}
    </div>
  );
}
